// AmsLegal.tsx — letters issued, fees added, and what has been paid since.
//
// `paymentSinceCents` is the column that matters most on this screen, and the reason it exists:
// a resident who pays after a demand has gone out must stop being chased, and the law firm has to
// be told. The monthly invoice reconciliation turns on exactly that, and a row highlighted here is
// a case where someone needs to act rather than a statistic.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError } from '../../lib/api'
import DataTable from '../DataTable'
import { dateOf, money } from '../../lib/format'

type Data = Awaited<ReturnType<typeof api.ams.legal>>

export default function AmsLegal() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!org) return
    api.ams.legal(org).then(setD)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }, [org])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  const fees = d.stages.reduce((s, x) => s + x.feesCents, 0)
  const paid = d.stages.reduce((s, x) => s + x.paidSinceCents, 0)
  const notNotified = d.stages.reduce((s, x) => s + x.notNotified, 0)

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Legal tracker</h2>
          <p className="muted">
            Letters are triggered here, sent by TCMS, and the law firm is notified of each issuance
            and each payment since.
          </p>
        </div>
      </header>

      <div className="grid-4">
        <div className="card kpi">
          <div className="kpi-label">Letters issued</div>
          <div className="kpi-n">{d.stages.reduce((s, x) => s + x.issued, 0)}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Fees added</div>
          <div className="kpi-n">{money(fees)}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Paid since a letter</div>
          <div className="kpi-n">{money(paid)}</div>
          <div className="muted sm">These residents must stop being chased</div>
        </div>
        <div className={`card kpi ${notNotified ? 'kpi-bad' : ''}`}>
          <div className="kpi-label">Law firm not notified</div>
          <div className="kpi-n">{notNotified}</div>
        </div>
      </div>

      {/* The reconciliation. Deliberately narrow and deliberately labelled: it compares the fee
          AMS recorded against the fee TCMS actually posted. It is NOT a check against a law firm's
          invoice — we hold no invoice data, and a screen claiming to have checked one would be
          inventing the most consequential fact on the page. */}
      <section className="card">
        <h3>Fee check — what we recorded against what TCMS charged</h3>
        <p className="muted sm">
          Across {d.feeCheck.scope}. {d.feeCheck.checked} checked.
        </p>
        {d.feeCheck.differenceCount === 0 ? (
          <div className="notice ok">
            <b>No differences.</b> Every fee AMS recorded matches one TCMS posted to the account
            within three days of the letter.
          </div>
        ) : (
          <>
            <div className="notice stop">
              <b>{d.feeCheck.differenceCount} difference
                {d.feeCheck.differenceCount === 1 ? '' : 's'}.</b>{' '}
              A fee we recorded is not the fee the resident was charged. Each one is either a
              letter whose fee never posted, or a charge the resident is carrying that no letter
              here accounts for.
            </div>
            <DataTable
              rows={d.feeCheck.differences}
              rowKey={x => x.letterId}
              empty="None."
              columns={[
                { key: 'resident', header: 'Resident', role: 'primary',
                  cell: x => <Link to={`/ams/cases/${x.caseId}`}>{x.residentName}</Link> },
                { key: 'acct', header: 'Account', role: 'secondary', cell: x => x.accountNo },
                { key: 'issued', header: 'Letter issued', cell: x => dateOf(x.issuedOn) },
                { key: 'ams', header: 'AMS recorded', num: true, cell: x => money(x.amsFeeCents) },
                { key: 'tcms', header: 'TCMS charged', num: true, cell: x => money(x.tcmsFeeCents) },
                { key: 'delta', header: 'Difference', num: true,
                  cell: x => <span className="tone-danger">{money(x.deltaCents)}</span> },
              ]}
            />
          </>
        )}
      </section>

      <section className="card">
        <h3>By stage</h3>
        <DataTable
          rows={d.stages}
          rowKey={s => s.stageKey}
          empty="No letters at any stage."
          columns={[
            { key: 'stage', header: 'Stage', role: 'primary',
              cell: s => <span className="tag stage">{s.stageName}</span> },
            { key: 'issued', header: 'Issued', num: true, cell: s => s.issued },
            { key: 'fees', header: 'Fees added', num: true, cell: s => money(s.feesCents) },
            { key: 'paid', header: 'Paid since', num: true,
              cell: s => <span className={s.paidSinceCents ? 'pos' : ''}>{money(s.paidSinceCents)}</span> },
            { key: 'nn', header: 'Not notified', num: true, cell: s => s.notNotified || '—' },
          ]}
        />
      </section>

      <section className="card">
        <h3>Recent letters</h3>
        {/* "Paid since" is the instruction on this screen — stop chasing, tell the firm — so it
            is the trailing token on a phone card rather than a figure buried mid-row. */}
        <DataTable
          rows={d.letters}
          rowKey={l => l.id}
          rowClass={l => (l.paymentSinceCents ? 'row-paid' : undefined)}
          empty="No letters issued."
          columns={[
            { key: 'resident', header: 'Resident', role: 'primary',
              cell: l => <Link to={`/ams/cases/${l.caseId}`}>{l.residentName}</Link> },
            { key: 'acct', header: 'Account', role: 'secondary',
              cell: l => `${l.accountNo} · ${l.division}` },
            { key: 'stage', header: 'Stage', role: 'trailing',
              cell: l => <span className="tag stage">{l.stageName}</span> },
            { key: 'owed', header: 'Owed', num: true, cell: l => money(l.balanceCents) },
            { key: 'days', header: 'Days', num: true, cell: l => l.daysOverdue },
            { key: 'fee', header: 'Fee', num: true,
              cell: l => <span className="muted">{money(l.feeCents)}</span> },
            { key: 'paid', header: 'Paid since', num: true,
              cell: l => (
                <span className={l.paymentSinceCents ? 'pos' : 'muted'}>
                  {l.paymentSinceCents ? money(l.paymentSinceCents) : '—'}
                </span>
              ) },
            { key: 'issued', header: 'Issued', cell: l => dateOf(l.issuedAt) },
            { key: 'firm', header: 'Law firm',
              cell: l => (l.lawFirmNotifiedAt ? dateOf(l.lawFirmNotifiedAt)
                : <span className="tone-danger">not notified</span>) },
          ]}
        />
      </section>
    </div>
  )
}
